import { Link, useLocation } from 'react-router-dom'
import {
  LayoutDashboard, FolderKanban, Globe, FileText, CreditCard,
  Shield, Users, Mail, DollarSign, Anchor, Ship, BarChart3,
  Briefcase, UserPlus, X,
} from 'lucide-react'
import { useAuth } from '@/hooks/use-auth'
import { useMySquadrons } from '@/hooks/api/use-squadrons'
import { cn } from '@/lib/utils'

interface HubSidebarProps {
  open: boolean
  onClose: () => void
}

interface NavItem {
  label: string
  href: string
  icon: React.ReactNode
  show?: boolean
}

interface NavGroup {
  title?: string
  items: NavItem[]
}

export function HubSidebar({ open, onClose }: HubSidebarProps) {
  const { pathname } = useLocation()
  const { user } = useAuth()
  const { data: squadronsData } = useMySquadrons()

  const isAdmin = user?.is_global_admin === true
  const hasSquadrons = (squadronsData?.squadrons?.length ?? 0) > 0

  const navGroups: NavGroup[] = [
    {
      items: [
        { label: 'Dashboard', href: '/hub', icon: <LayoutDashboard className="h-4 w-4" /> },
        { label: 'Projects', href: '/hub/projects', icon: <FolderKanban className="h-4 w-4" /> },
        { label: 'Billing', href: '/hub/billing', icon: <CreditCard className="h-4 w-4" /> },
      ],
    },
    {
      title: 'Command',
      items: [
        { label: "Admiral's Bridge", href: '/hub/admiral/squadrons', icon: <Anchor className="h-4 w-4" />, show: hasSquadrons },
        { label: 'Fleet Captain', href: '/hub/captain/fleet', icon: <Ship className="h-4 w-4" /> },
      ],
    },
    {
      title: 'Admin',
      items: [
        { label: 'Fleet Command', href: '/hub/admin/fleet', icon: <Shield className="h-4 w-4" />, show: isAdmin },
        { label: 'Squadrons', href: '/hub/admin/squadrons', icon: <Ship className="h-4 w-4" />, show: isAdmin },
        { label: 'Finances', href: '/hub/admin/finances', icon: <DollarSign className="h-4 w-4" />, show: isAdmin },
        { label: 'Recruitment', href: '/hub/admin/recruitment', icon: <Briefcase className="h-4 w-4" />, show: isAdmin },
        { label: 'Users', href: '/hub/admin/users', icon: <Users className="h-4 w-4" />, show: isAdmin },
        { label: 'Invites', href: '/hub/admin/invites', icon: <Mail className="h-4 w-4" />, show: isAdmin },
      ],
    },
  ]

  const filteredGroups = navGroups
    .map(group => ({
      ...group,
      items: group.items.filter(item => item.show !== false),
    }))
    .filter(group => group.items.length > 0)

  function isActive(href: string) {
    if (href === '/hub') return pathname === '/hub'
    return pathname.startsWith(href)
  }

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={onClose}
        />
      )}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex w-60 flex-col border-r border-border bg-background transition-transform duration-200 lg:static lg:translate-x-0',
          open ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex h-14 items-center justify-between border-b border-border px-4 lg:hidden">
          <span className="font-heading text-lg font-semibold">
            New<span className="text-primary">BX</span>
          </span>
          <button onClick={onClose} className="rounded p-1 hover:bg-accent">
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4">
          {filteredGroups.map((group, gi) => (
            <div key={gi} className={cn(gi > 0 && 'mt-6')}>
              {group.title && (
                <h4 className="mb-1 px-3 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                  {group.title}
                </h4>
              )}
              <ul className="space-y-0.5">
                {group.items.map((item) => (
                  <li key={item.href}>
                    <Link
                      to={item.href}
                      onClick={onClose}
                      className={cn(
                        'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                        isActive(item.href)
                          ? 'bg-accent text-foreground'
                          : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground'
                      )}
                    >
                      {item.icon}
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        <div className="border-t border-border p-4">
          <div className="text-[11px] text-muted-foreground">
            NewBX Lab v8.0
          </div>
        </div>
      </aside>
    </>
  )
}
